import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { contactApi, type ContactView, type RepView } from "../api/contact";

/**
 * Customer Contact page — single-pane view of who to call at Bankom.
 *
 * Shows the Bankom company card (address, VAT, website) plus the
 * caller's assigned sales reps and quality-control rep with name, email,
 * phone, avatar. Source of truth lives in the company / sales-scope tables;
 * this page is just a presentation layer over the /api/contact/principal
 * endpoint, which already does the union of direct + scope-based reps.
 */
export default function ContactPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<ContactView | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    contactApi
      .getPrincipal()
      .then((res) => mounted && setData(res))
      .catch((e) => mounted && setErr(e?.message ?? String(e)));
    return () => {
      mounted = false;
    };
  }, []);

  if (err) {
    return (
      <div className="card">
        <div className="card-b" style={{ padding: 24, color: "var(--err)" }}>{err}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="card">
        <div className="card-b" style={{ padding: 24, color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</div>
      </div>
    );
  }

  const { principal, salesReps, qualityRep } = data;

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="page-head">
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: "var(--ink)" }}>
            {t("contact.title", "Contact")}
          </h1>
          <div className="sub">
            {t("contact.subtitle", "Vaša direktna veza sa Bankom — sedište, prodajni tim i podrška za kvalitet.")}
          </div>
        </div>
      </div>

      <div className="card">
        <header className="card-h">
          <strong>{t("contact.principal.title", "Bankom d.o.o.")}</strong>
        </header>
        <div className="card-b" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Row label={t("contact.principal.name", "Company")} value={principal.name} />
          <Row label={t("contact.principal.vat", "VAT")} value={principal.vatNumber ?? "—"} />
          <Row
            label={t("contact.principal.address", "Address")}
            value={[principal.addressLine1, principal.addressLine2].filter(Boolean).join(", ") || "—"}
          />
          <Row
            label={t("contact.principal.city", "City")}
            value={
              [principal.postalCode, principal.city, principal.country].filter(Boolean).join(" ") || "—"
            }
          />
          <Row
            label={t("contact.principal.website", "Website")}
            value={
              principal.website ? (
                <a href={principal.website} target="_blank" rel="noreferrer" style={{ color: "var(--brand-primary)" }}>
                  {principal.website}
                </a>
              ) : (
                "—"
              )
            }
          />
        </div>
      </div>

      <div className="card">
        <header className="card-h">
          <strong>{t("contact.sales.title", "Sales representatives")}</strong>
          <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
            {salesReps.length}
          </span>
        </header>
        <div className="card-b" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {salesReps.length === 0 ? (
            <div className="muted" style={{ fontSize: 13 }}>
              {t("contact.sales.empty", "No sales representative assigned yet.")}
            </div>
          ) : (
            salesReps.map((rep) => <RepCard key={rep.userId} rep={rep} />)
          )}
        </div>
      </div>

      <div className="card">
        <header className="card-h">
          <strong>{t("contact.quality.title", "Quality control")}</strong>
        </header>
        <div className="card-b">
          {qualityRep ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
              <RepCard rep={qualityRep} />
            </div>
          ) : (
            <div className="muted" style={{ fontSize: 13 }}>
              {t("contact.quality.empty", "No quality control representative assigned yet.")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span className="muted" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
      <span style={{ color: "var(--ink)", fontSize: 14 }}>{value}</span>
    </div>
  );
}

function RepCard({ rep }: { rep: RepView }) {
  const initials = rep.displayName
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: 8,
        padding: 12,
        display: "flex",
        gap: 12,
        alignItems: "center",
        background: "var(--surface)",
      }}
    >
      <Avatar url={rep.avatarUrl} initials={initials} />
      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <strong style={{ color: "var(--ink)", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {rep.displayName}
        </strong>
        <a href={`mailto:${rep.email}`} style={{ color: "var(--brand-primary)", fontSize: 13, textDecoration: "none" }}>
          {rep.email}
        </a>
        {rep.phone ? (
          <a href={`tel:${rep.phone}`} style={{ color: "var(--ink-2)", fontSize: 13, textDecoration: "none" }}>
            {rep.phone}
          </a>
        ) : (
          <span className="muted" style={{ fontSize: 12 }}>—</span>
        )}
      </div>
    </div>
  );
}

function Avatar({ url, initials }: { url: string | null; initials: string }) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        style={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          objectFit: "cover",
          flex: "none",
          border: "1px solid var(--border)",
        }}
      />
    );
  }
  return (
    <div
      aria-hidden
      style={{
        width: 48,
        height: 48,
        borderRadius: "50%",
        background: "var(--brand-primary)",
        color: "var(--on-primary)",
        display: "grid",
        placeItems: "center",
        fontWeight: 600,
        fontSize: 14,
        flex: "none",
      }}
    >
      {initials || "—"}
    </div>
  );
}
